/**
 * Recipe API - Doctor Recipe CRUD Operations
 * Handles creating, updating, submitting remedies for review, and listing recipes
 */

import http from '../../../client';
import { errorHandler } from '../../../errorHandler';
import type { ApiResponse } from '../../../types';
import type {
  Recipe,
  CreateRecipeData,
  DoctorRecipe,
  RecipesPagination,
  GetRecipesApiResponse,
  ActivityLogResponse,
} from './types';
import type {
  RecipeValidationResult,
  SubmitValidationError,
} from '../../../../types/validation';
import { extractResponseData } from './helpers';
import { transformRecipeFromBackend } from './transformers';
import { uploadFileDirectToAzure } from './blobUpload';
import {
  newRecipeTraceId,
  noteOpenTrace,
  recordRecipeTrace,
  snapshotForTrace,
} from '../../../../utils/recipeDataTraceClient';

export type RecipeSaveUploadOptions = {
  /** 0–100 while direct Azure uploads run (before JSON create/update). */
  onUploadProgress?: (percent: number) => void;
  /** `null` saves as personal. A team id saves on that team. */
  teamScope?: string | null;
  /** Same key for every create attempt while this form is open. Not sent on update. */
  idempotencyKey?: string;
};

export const prepareDirectUploadedMedia = async (
  data: CreateRecipeData,
  recipeId?: string | number,
  options?: RecipeSaveUploadOptions,
): Promise<CreateRecipeData> => {
  const next: CreateRecipeData = { ...data };
  const onUploadProgress = options?.onUploadProgress;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getFile = (val: any): File | null => {
    if (val instanceof File) return val;
    if (
      val &&
      typeof val === 'object' &&
      'length' in val &&
      val.length > 0 &&
      val[0] instanceof File
    )
      return val[0];
    return null;
  };

  const galleryFiles =
    Array.isArray(next.cover_gallery_images) &&
    next.cover_gallery_images.length > 0
      ? next.cover_gallery_images.filter((f) => f instanceof File)
      : [];

  const jobSizes: number[] = [];
  const coverImageFile = getFile(next.cover_image);
  if (coverImageFile) {
    jobSizes.push(Math.max(1, coverImageFile.size));
  }
  for (const f of galleryFiles) {
    jobSizes.push(Math.max(1, f.size));
  }
  const coverVideoFile = getFile(next.cover_video);
  if (coverVideoFile) {
    jobSizes.push(Math.max(1, coverVideoFile.size));
  }
  const prepVideoFile = getFile(next.preparation_video);
  if (prepVideoFile) {
    jobSizes.push(Math.max(1, prepVideoFile.size));
  }

  // Find nested prep files
  const prepVideoFiles: { file: File; prepIndex: number }[] = [];
  const stepMediaFiles: {
    file: File;
    prepIndex: number;
    stepIndex: number;
    type: 'image' | 'video';
  }[] = [];

  if (Array.isArray(next.preparations)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    next.preparations.forEach((prep: any, pIdx: number) => {
      const nestedPrepVideo = getFile(prep.preparation_video);
      if (nestedPrepVideo) {
        prepVideoFiles.push({ file: nestedPrepVideo, prepIndex: pIdx });
        jobSizes.push(Math.max(1, nestedPrepVideo.size));
      }
      if (Array.isArray(prep.preparation_steps)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        prep.preparation_steps.forEach((step: any, sIdx: number) => {
          // Step Image
          const stepImageFile =
            step.image instanceof File
              ? step.image
              : step.image &&
                  typeof step.image === 'object' &&
                  'length' in step.image &&
                  step.image.length > 0 &&
                  step.image[0] instanceof File
                ? step.image[0]
                : null;

          if (stepImageFile) {
            stepMediaFiles.push({
              file: stepImageFile,
              prepIndex: pIdx,
              stepIndex: sIdx,
              type: 'image',
            });
            jobSizes.push(Math.max(1, stepImageFile.size));
          }

          // Step Video
          const stepVideoFile =
            step.video instanceof File
              ? step.video
              : step.video &&
                  typeof step.video === 'object' &&
                  'length' in step.video &&
                  step.video.length > 0 &&
                  step.video[0] instanceof File
                ? step.video[0]
                : null;

          if (stepVideoFile) {
            stepMediaFiles.push({
              file: stepVideoFile,
              prepIndex: pIdx,
              stepIndex: sIdx,
              type: 'video',
            });
            jobSizes.push(Math.max(1, stepVideoFile.size));
          }
        });
      }
    });
  }

  const loaded: number[] = new Array(jobSizes.length).fill(0);
  const totalBytes = jobSizes.reduce((a, b) => a + b, 0) || 1;
  if (onUploadProgress && jobSizes.length > 0) {
    onUploadProgress(0);
  }
  const mkAt = (index: number) => {
    if (!onUploadProgress || jobSizes.length === 0) return undefined;
    return (percent: number) => {
      loaded[index] = (jobSizes[index] * Math.min(100, percent)) / 100;
      const sum = loaded.reduce((a, b) => a + b, 0);
      onUploadProgress(Math.min(100, (sum / totalBytes) * 100));
    };
  };

  let idx = 0;

  if (coverImageFile) {
    const coverUrl = await uploadFileDirectToAzure(coverImageFile, {
      mediaType: 'cover',
      recipeId,
      onProgress: mkAt(idx++),
    });
    next.cover_image_url = coverUrl;
    next.existing_cover_image_url = coverUrl;
    next.cover_image = coverUrl;
  }

  if (galleryFiles.length > 0) {
    const uploadedGalleryUrls = await Promise.all(
      galleryFiles.map((file, k) =>
        uploadFileDirectToAzure(file, {
          mediaType: 'gallery',
          recipeId,
          onProgress: mkAt(idx + k),
        }),
      ),
    );
    idx += galleryFiles.length;
    next.cover_gallery_image_urls = uploadedGalleryUrls;
    next.existing_cover_gallery_urls = [
      ...(Array.isArray(next.existing_cover_gallery_urls)
        ? next.existing_cover_gallery_urls
        : []),
      ...uploadedGalleryUrls,
    ];
    next.cover_gallery_images = [];
  }

  if (coverVideoFile) {
    const coverVideoUrl = await uploadFileDirectToAzure(coverVideoFile, {
      mediaType: 'cover-video',
      recipeId,
      onProgress: mkAt(idx++),
    });
    next.cover_video_url = coverVideoUrl;
    next.existing_cover_video_url = coverVideoUrl;
    next.cover_video = coverVideoUrl;
  }

  if (prepVideoFile) {
    const preparationVideoUrl = await uploadFileDirectToAzure(prepVideoFile, {
      mediaType: 'preparation-video',
      recipeId,
      onProgress: mkAt(idx++),
    });
    next.preparation_video_url = preparationVideoUrl;
    delete next.preparation_video;
  }

  // Process nested prep files
  for (const item of prepVideoFiles) {
    const url = await uploadFileDirectToAzure(item.file, {
      mediaType: 'preparation-video',
      recipeId,
      onProgress: mkAt(idx++),
    });
    next.preparations![item.prepIndex].preparation_video_url = url;
    next.preparations![item.prepIndex].preparation_video = url; // or delete? Keep URL for form payload
  }

  for (const item of stepMediaFiles) {
    const url = await uploadFileDirectToAzure(item.file, {
      mediaType: item.type === 'image' ? 'step-image' : 'step-video',
      recipeId,
      onProgress: mkAt(idx++),
    });
    if (item.type === 'image') {
      next.preparations![item.prepIndex].preparation_steps[
        item.stepIndex
      ].image = url;
      next.preparations![item.prepIndex].preparation_steps[
        item.stepIndex
      ].image_url = url; // Keep for frontend consistency if needed
    } else {
      next.preparations![item.prepIndex].preparation_steps[
        item.stepIndex
      ].video = url;
      next.preparations![item.prepIndex].preparation_steps[
        item.stepIndex
      ].video_url = url;
    }
  }

  return next;
};

/**
 * Creates a new remedy
 * Endpoint: POST /recipes
 * @param data - Remedy data including title, description, files, etc.
 * @returns Created recipe object
 */
export const createRecipe = async (
  data: CreateRecipeData,
  uploadOptions?: RecipeSaveUploadOptions,
): Promise<ApiResponse<Recipe>> => {
  try {
    // We expect the caller to have already run prepareDirectUploadedMedia
    // but just in case, we'll run it again (it does nothing if no Files are present).
    const preparedData = await prepareDirectUploadedMedia(
      data,
      undefined,
      uploadOptions,
    );
    const response = await http.post('/recipes', preparedData, {
      teamScope: uploadOptions?.teamScope,
      headers: uploadOptions?.idempotencyKey
        ? { 'X-Idempotency-Key': uploadOptions.idempotencyKey }
        : undefined,
    });

    const recipeData = extractResponseData<Recipe>(response);
    const alert = (
      response.data as {
        data_loss_alert?: { message: string; missing: string[] } | null;
      }
    )?.data_loss_alert;

    return {
      success: true,
      data: recipeData,
      dataLossAlert: alert || null,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

export const deleteRecipe = async (
  recipeId: string | number,
): Promise<ApiResponse<void>> => {
  try {
    const recipeIdStr = String(recipeId);
    const response = await http.delete(`/recipes/${recipeIdStr}`);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Updates an existing recipe
 * Endpoint: PUT /recipes/{recipeId}
 * @param recipeId - ID of the remedy to update
 * @param data - Updated remedy data
 * @returns Updated recipe object
 */
export const updateRecipe = async (
  recipeId: string | number,
  data: CreateRecipeData,
  uploadOptions?: RecipeSaveUploadOptions,
): Promise<ApiResponse<Recipe>> => {
  try {
    const recipeIdStr = String(recipeId);

    // Validate title is present and not empty
    const title = String(data.title || '').trim();
    if (!title) {
      return {
        success: false,
        error: 'Title is required and cannot be empty.',
      };
    }

    // We expect the caller to have already run prepareDirectUploadedMedia
    const preparedData = await prepareDirectUploadedMedia(
      { ...data, title },
      recipeId,
      uploadOptions,
    );
    const response = await http.put(`/recipes/${recipeIdStr}`, preparedData, {
      teamScope: uploadOptions?.teamScope,
    });

    const recipeData = extractResponseData<Recipe>(response);
    const alert = (
      response.data as {
        data_loss_alert?: { message: string; missing: string[] } | null;
      }
    )?.data_loss_alert;

    return {
      success: true,
      data: recipeData,
      dataLossAlert: alert || null,
    };
  } catch (error: unknown) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const err = error as any;
    // Intercept 409 Version Conflict and pass the structured conflict data back
    if (
      err?.response?.status === 409 &&
      err?.response?.data?.error === 'VERSION_CONFLICT'
    ) {
      return {
        success: false,
        error:
          err.response.data.message ||
          'Remedy was modified in another session.',
        conflictData: err.response.data.data,
      };
    }
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Submits a remedy for admin review
 * Endpoint: POST /recipes/{recipeId}/submit-for-review
 * @param recipeId - ID of the remedy to submit
 * @returns Updated recipe object with status changed or validation errors
 */
export const submitForReview = async (
  recipeId: string | number,
  options?: Pick<RecipeSaveUploadOptions, 'teamScope'>,
): Promise<ApiResponse<Recipe | SubmitValidationError>> => {
  try {
    const recipeIdStr = String(recipeId);

    const response = await http.post(
      `/recipes/${recipeIdStr}/submit-for-review`,
      undefined,
      { teamScope: options?.teamScope },
    );

    const recipeData = extractResponseData<Recipe>(response);

    return {
      success: true,
      data: recipeData,
    };
  } catch (error: unknown) {
    // Check if it's a validation error (400 status with validation data)
    if (
      typeof error === 'object' &&
      error !== null &&
      'response' in error &&
      typeof (error as { response?: { status?: number; data?: unknown } })
        .response === 'object'
    ) {
      const axiosError = error as {
        response?: { status?: number; data?: unknown };
      };
      if (
        axiosError.response?.status === 400 &&
        axiosError.response?.data &&
        typeof axiosError.response.data === 'object' &&
        axiosError.response.data !== null &&
        'validation' in axiosError.response.data
      ) {
        // Return validation error - put validation error in data field, message in error field
        const validationError = axiosError.response
          .data as SubmitValidationError;
        return {
          success: false,
          error: validationError.message || 'Validation failed',
          data: validationError,
        };
      }
    }

    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Validate recipe before submission
 * Endpoint: GET /recipes/{recipeId}/validate
 * @param recipeId - ID of the remedy to validate
 * @param detailed - Whether to return detailed validation (default: true)
 * @returns Validation result with step-by-step errors
 */
export const validateRecipe = async (
  recipeId: string | number,
  detailed = true,
): Promise<ApiResponse<RecipeValidationResult>> => {
  try {
    const recipeIdStr = String(recipeId);
    const response = await http.get(
      `/recipes/${recipeIdStr}/validate?detailed=${detailed}`,
    );

    const validationData =
      extractResponseData<RecipeValidationResult>(response);

    return {
      success: true,
      data: validationData,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Check if recipe can be submitted
 * Endpoint: GET /recipes/{recipeId}/can-submit
 * @param recipeId - ID of the remedy to check
 * @returns Quick check result with completion percentage
 */
export const canSubmitRecipe = async (
  recipeId: string | number,
): Promise<
  ApiResponse<{
    canSubmit: boolean;
    message: string;
    errors: string[];
    warnings: string[];
    completionPercentage: number;
  }>
> => {
  try {
    const recipeIdStr = String(recipeId);
    const response = await http.get(`/recipes/${recipeIdStr}/can-submit`);

    const canSubmitData = extractResponseData<{
      canSubmit: boolean;
      message: string;
      errors: string[];
      warnings: string[];
      completionPercentage: number;
    }>(response);

    return {
      success: true,
      data: canSubmitData,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Gets a single recipe by ID
 * Endpoint: GET /recipes/{recipeId}
 * @param recipeId - ID of the remedy to fetch
 * @returns Recipe object
 */
export const getRecipeById = async (
  recipeId: string | number,
  options?: { trace?: boolean },
): Promise<ApiResponse<Recipe>> => {
  try {
    const recipeIdStr = String(recipeId);

    const openTraceId = options?.trace ? newRecipeTraceId() : null;
    const response = await http.get(`/recipes/${recipeIdStr}`, {
      params: openTraceId
        ? { t: Date.now(), trace: 1, traceId: openTraceId }
        : { t: Date.now() },
    });

    const rawRecipeData = extractResponseData(response);

    // Transform backend format to frontend format
    const recipeData = transformRecipeFromBackend(rawRecipeData);

    // Extract dosha_ids from raw data before transformation
    const rawDoshas = (
      rawRecipeData as {
        doshas?: Array<{ id?: string | number; name?: string }>;
      }
    ).doshas;
    const doshaIds =
      rawDoshas
        ?.map((dosha) => {
          if (typeof dosha === 'object' && dosha.id) {
            const id =
              typeof dosha.id === 'string' ? Number(dosha.id) : dosha.id;
            return !isNaN(id) ? id : null;
          }
          return null;
        })
        .filter((id): id is number => id !== null) || [];

    // Extract tag IDs from raw data before transformation
    const rawTags = (
      rawRecipeData as {
        tags?: Array<{ id?: string | number; name?: string } | string>;
      }
    ).tags;
    const tagIds =
      rawTags
        ?.map((tag) => {
          if (typeof tag === 'object' && tag.id) {
            const id = typeof tag.id === 'string' ? Number(tag.id) : tag.id;
            return !isNaN(id) ? id : null;
          }
          return null;
        })
        .filter((id): id is number => id !== null) || [];
    const tagId = tagIds.length > 0 ? tagIds[0] : null;

    // Extract season from raw data before transformation
    const rawSeasons = (
      rawRecipeData as {
        seasons?: Array<{ id?: string | number; name?: string } | string>;
      }
    ).seasons;
    const seasonValue =
      rawSeasons && rawSeasons.length > 0
        ? typeof rawSeasons[0] === 'string'
          ? rawSeasons[0]
          : rawSeasons[0]?.name || ''
        : undefined;

    // Extract calorie from raw data
    const calorie = (rawRecipeData as { calorie?: number | string }).calorie;
    const calorieValue =
      calorie !== undefined
        ? typeof calorie === 'string'
          ? Number(calorie)
          : calorie
        : undefined;

    // Preserve steps, approvals, adminNotes, internal_name, dosha_ids, tag_id, season, and calorie from raw data
    // Note: transformer returns 'seasons' (plural) but Recipe type uses 'season' (singular)
    const transformedSeasons =
      (recipeData as { seasons?: string[] }).seasons || [];
    const raw = rawRecipeData as {
      recommended_diet?: string | null;
      diet_to_avoid?: string | null;
      general_exercise_notes?: string | null;
      exercises?: unknown[];
      custom_list_values?: Record<string, string[]> | null;
    };
    // Explicitly pull diet fields from raw (backend) - ensure edit prefill works
    const recommendedDiet =
      (recipeData as { recommended_diet?: string }).recommended_diet ??
      raw.recommended_diet ??
      '';
    const dietToAvoid =
      (recipeData as { diet_to_avoid?: string }).diet_to_avoid ??
      raw.diet_to_avoid ??
      '';
    const finalData = {
      ...recipeData,
      dosha_ids:
        doshaIds.length > 0
          ? doshaIds.map(String)
          : (recipeData as { dosha_ids?: string[] }).dosha_ids || [],
      tag_id: tagId !== null ? String(tagId) : undefined,
      season: seasonValue ? [seasonValue] : transformedSeasons,
      calorie: !isNaN(calorieValue || NaN) ? calorieValue : undefined,
      internal_name:
        (rawRecipeData as { internal_name?: string }).internal_name ||
        recipeData.title ||
        '',
      steps: recipeData.steps || (rawRecipeData as { steps?: unknown }).steps,
      approvals:
        recipeData.approvals ||
        (rawRecipeData as { approvals?: unknown }).approvals,
      adminNotes:
        recipeData.adminNotes ||
        (rawRecipeData as { adminNotes?: unknown }).adminNotes,
      recommended_diet:
        recommendedDiet != null && recommendedDiet !== undefined
          ? String(recommendedDiet)
          : '',
      diet_to_avoid:
        dietToAvoid != null && dietToAvoid !== undefined
          ? String(dietToAvoid)
          : '',
      general_exercise_notes:
        (recipeData as { general_exercise_notes?: string })
          .general_exercise_notes ??
        raw.general_exercise_notes ??
        '',
      custom_list_values:
        (recipeData as { custom_list_values?: Record<string, string[]> })
          .custom_list_values ??
        raw.custom_list_values ??
        undefined,
      exercises:
        (recipeData as { exercises?: unknown[] }).exercises ??
        raw.exercises ??
        [],
    };

    if (openTraceId) {
      noteOpenTrace(recipeIdStr, openTraceId);
      await recordRecipeTrace({
        traceId: openTraceId,
        recipeId: recipeIdStr,
        action: 'open',
        stages: [
          {
            stage: 'api_received',
            snapshot: snapshotForTrace(rawRecipeData),
            note: 'JSON the server sent',
          },
          {
            stage: 'api_transformed',
            snapshot: snapshotForTrace(finalData),
            note: 'After the browser converted the server JSON into the form shape',
          },
        ],
      });
    }

    return {
      success: true,
      data: finalData as Recipe,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Gets all remedies for the current doctor
 * Endpoint: GET /recipes/my-recipes
 * @param params - Query parameters (page, limit, search)
 * @returns Paginated list of recipes
 */
export const getAllRecipes = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  createdBy?: string;
  workspace?: string;
}): Promise<
  ApiResponse<{
    recipes: DoctorRecipe[];
    members: { id: string; name: string }[];
    statusFilters: { value: string; label: string }[];
    pagination: RecipesPagination;
  }>
> => {
  try {
    const response = await http.get('/recipes/my-recipes', { params });
    const apiResponse = response.data as GetRecipesApiResponse;
    // Extract recipes array and pagination directly from the known structure
    const recipes = Array.isArray(apiResponse?.data) ? apiResponse.data : [];
    const pagination = apiResponse?.pagination || {
      total: recipes.length,
      page: params?.page ?? 1,
      limit: params?.limit ?? 10,
      totalPages: Math.max(
        1,
        Math.ceil((recipes.length || 0) / (params?.limit ?? 10)),
      ),
    };
    return {
      success: true,
      data: {
        recipes,
        members: Array.isArray(apiResponse?.members) ? apiResponse.members : [],
        statusFilters: Array.isArray(apiResponse?.statusFilters)
          ? apiResponse.statusFilters
          : [],
        pagination: {
          total: pagination.total,
          page: pagination.page,
          limit: pagination.limit,
          totalPages: pagination.totalPages,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Add a common mistake to a remedy
 * Endpoint: POST /recipes/{recipeId}/common-mistakes
 */
export const addCommonMistake = async (
  recipeId: string | number,
  data: { mistake: string; correction: string; index?: number },
): Promise<ApiResponse<{ mistake: string; correction: string }[]>> => {
  try {
    const recipeIdStr = String(recipeId);
    const response = await http.post(
      `/recipes/${recipeIdStr}/common-mistakes`,
      data,
    );
    const dataRes =
      extractResponseData<{ mistake: string; correction: string }[]>(response);
    return {
      success: true,
      data: dataRes,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Reverts an edited recipe draft to its parent version
 * Endpoint: POST /recipes/{recipeId}/revert
 */
export const revertRecipe = async (
  recipeId: string | number,
): Promise<ApiResponse<unknown>> => {
  try {
    const recipeIdStr = String(recipeId);
    const response = await http.post(`/recipes/${recipeIdStr}/revert`);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Archives a remedy
 * Endpoint: POST /recipes/{recipeId}/archive
 */
export const archiveRecipe = async (
  recipeId: string | number,
): Promise<ApiResponse<unknown>> => {
  try {
    const recipeIdStr = String(recipeId);
    const response = await http.post(`/recipes/${recipeIdStr}/archive`);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Gets activity logs for a remedy
 * Endpoint: GET /recipes/{recipeId}/activity-logs
 */
export const getRecipeActivityLogs = async (
  recipeId: string | number,
): Promise<ApiResponse<ActivityLogResponse['data']>> => {
  try {
    const recipeIdStr = String(recipeId);
    const response = await http.get(`/recipes/${recipeIdStr}/activity-logs`);
    return {
      success: true,
      data: response.data.data,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Gets approved recipes (for doctor tools/shared views)
 * Endpoint: GET /recipes/approved
 * Note: This is a lightweight wrapper around the public approved-recipes endpoint.
 */
export const getApprovedRecipes = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
}): Promise<
  ApiResponse<{
    recipes: Recipe[];
    pagination: RecipesPagination;
  }>
> => {
  try {
    const response = await http.get('/recipes/approved', { params });
    const apiResponse = response.data as {
      data?: Recipe[];
      pagination?: RecipesPagination;
    };

    const recipes = Array.isArray(apiResponse.data) ? apiResponse.data : [];
    const pagination = apiResponse.pagination || {
      total: recipes.length,
      page: params?.page ?? 1,
      limit: params?.limit ?? 20,
      totalPages: Math.max(
        1,
        Math.ceil((recipes.length || 0) / (params?.limit ?? 20)),
      ),
    };

    return {
      success: true,
      data: {
        recipes,
        pagination: {
          total: pagination.total,
          page: pagination.page,
          limit: pagination.limit,
          totalPages: pagination.totalPages,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Gets approved remedies for parent recipe dropdown (id + title + author)
 * Endpoint: GET /recipes/parent-options
 * @param params - Query parameters (page, limit, search)
 * @returns Paginated list of approved remedies with id, title, and author name
 */
export const getParentRecipeOptions = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
}): Promise<
  ApiResponse<{
    recipes: Array<{
      id: string | number;
      title: string;
      name?: string;
      authorName?: string | null;
    }>;
    pagination: RecipesPagination;
  }>
> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);

    const response = await http.get(
      `/recipes/parent-options?${queryParams.toString()}`,
    );
    const apiResponse = response.data;

    // Extract recipes array - handle different response structures
    let recipes: Array<{
      id: string | number;
      title: string;
      name?: string;
      authorName?: string | null;
    }> = [];
    const rawRecipes =
      apiResponse?.data?.recipes && Array.isArray(apiResponse.data.recipes)
        ? apiResponse.data.recipes
        : apiResponse?.data && Array.isArray(apiResponse.data)
          ? apiResponse.data
          : [];

    recipes = rawRecipes.map((recipe: unknown) => {
      const r = recipe as
        | (Recipe & { author?: { name?: string; full_name?: string } })
        | {
            id?: string | number;
            uuid?: string;
            title?: string;
            internal_name?: string;
            authorName?: string | null;
          };

      const id = r.id || (r as { uuid?: string }).uuid || '';
      const title =
        (r as { title?: string }).title ||
        (r as { internal_name?: string }).internal_name ||
        '';

      const directAuthorName =
        (r as { authorName?: string | null }).authorName ?? null;

      const relatedAuthor = (
        r as { author?: { name?: string; full_name?: string } }
      ).author;

      const derivedAuthorName =
        (relatedAuthor && (relatedAuthor.name || relatedAuthor.full_name)) ||
        null;

      return {
        id,
        title,
        name: (r as { internal_name?: string }).internal_name,
        authorName: directAuthorName ?? derivedAuthorName,
      };
    });

    const pagination = apiResponse?.pagination || {
      total: recipes.length,
      page: params?.page ?? 1,
      limit: params?.limit ?? 100,
      totalPages: Math.max(
        1,
        Math.ceil((recipes.length || 0) / (params?.limit ?? 100)),
      ),
    };

    return {
      success: true,
      data: {
        recipes,
        pagination: {
          total: pagination.total,
          page: pagination.page,
          limit: pagination.limit,
          totalPages: pagination.totalPages,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Takes back a remedy, moving it out of the review queue and returning it to draft
 */
export const takeBackRecipe = async (
  recipeId: string | number,
): Promise<ApiResponse<Recipe>> => {
  try {
    const response = await http.post(`/recipes/${recipeId}/take-back`);
    return {
      success: true,
      data: response.data?.data || response.data,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Updates which list a remedy lives on.
 * personal = team owner's personal list. team = attach to teamId.
 */
export const updateRecipeScope = async (
  recipeId: string | number,
  visibility_scope: 'personal' | 'team',
  teamId?: string | number | null,
): Promise<ApiResponse<Recipe>> => {
  try {
    const response = await http.put(`/recipes/${recipeId}/scope`, {
      visibility_scope,
      ...(teamId != null && teamId !== '' ? { team_id: teamId } : {}),
    });
    const recipeData = extractResponseData<Recipe>(response);
    return { success: true, data: recipeData };
  } catch (error: any) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/** Mark a team remedy ready for the owner, or set it back to draft. */
export const setRecipeReady = async (
  recipeId: string | number,
  status: 'ready' | 'draft',
): Promise<ApiResponse<Recipe>> => {
  try {
    const response = await http.put(`/recipes/${recipeId}/ready`, { status });
    const recipeData = extractResponseData<Recipe>(response);
    return { success: true, data: recipeData };
  } catch (error: any) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};
